import { ReportingTypeEnum } from '@quadminds/shared/commons';
import { EventTypeEnum, TachographDriverStatus } from '@quadminds/shared/tracking-system';
import { IAccountSchema } from './account-schema.interface';
import { ICarrierSchema } from './carrier-schema.interface';
import { ICheckpointSchema, TriggerOrder } from './checkpoint-schema.interface';
import { IDriverSchema } from './driver-schema.interface';
import { IEventSchema } from './event-schema.interface';
import { ISemitrailerSchema } from './semitrailer-schema.interface';
import { ITagsSchema } from './tags-schema.interface';
import { IVehicleSchema } from './vehicle-schema.interface';
import { TripErrorCodeEnum } from '../../enums';

export interface ITripErrors {
    code: TripErrorCodeEnum;
    message: string;
    metadata?: Record<string, any>;
}

interface UnitValueSchema {
    unit: string;
    value: string;
}

interface TripRouteSchema {
    distanceToGo: UnitValueSchema;
    endsIn: UnitValueSchema;
    distanceTraveled: UnitValueSchema;
    timeTraveled: UnitValueSchema;
    remainingPath: string[];
    traveledPath: string[];
    positions?: {
        lat: string;
        long: string;
        heading: number;
        speed: number;
        timestamp: string;
    }[];
    expectedRoute?: string[];
}

/**
 * How a service type travels inside a trip: enough to label it, nothing else. The full record,
 * with its units and audit trail, belongs to the service types endpoints.
 */
export interface ITripServiceTypeSchema {
    id: string;
    code: string;
    name: string;
}

/** One service billed on the trip, as shown in the trip views. */
export interface ITripServiceSchema {
    serviceType: ITripServiceTypeSchema;
    measureUnit?: string;
    quantity?: number;
}

export interface ITripSchema {
    id: string;
    organizationId?: string | null;
    tripDefinition: string;
    delayedYellow?: string | null;
    delayedRed?: string | null;
    pendingColor?: string | null;
    finishedColor?: string | null;
    scheduledStart: string;
    scheduledFinish?: string | null;
    realStart: string;
    realFinish: string;
    finishReason?: string;
    finishBy?: string;
    locale: string;
    tripLifetime?: string | null;
    tripStartedLifetime?: string | null;
    vehicle?: IVehicleSchema | null;
    semitrailers?: ISemitrailerSchema[] | null;
    driver?: IDriverSchema | null;
    companion?: IDriverSchema | null;
    carrier?: ICarrierSchema | null;
    /** Section 1's unit; the fields above hold the unit currently in charge. Refs may be id-only. */
    originalUnit?: {
        carrier?: ICarrierSchema | null;
        vehicle?: IVehicleSchema | null;
        driver?: IDriverSchema | null;
        semitrailers?: ISemitrailerSchema[] | null;
    } | null;
    tripCode: string;
    terminalName?: string | null;
    accounts?: IAccountSchema[] | null;
    checkpoints: ICheckpointSchema[];
    meters?: number | null;
    theoreticalMeters?: number | null;
    isTheoretical?: boolean | null;
    minPercentage?: number | null;
    maxPercentage?: number | null;
    maxDrivingTime?: number | null;
    restingTime?: number | null;
    maxDailyDrivingTime?: number | null;
    drivingTimeWindowTolerance?: number | null;
    metersWithoutOptions?: number | null;
    finishOnLastCheckpointStep?: boolean | null;
    forceShowLastCheckpointNext: boolean;
    stopped?: boolean | null;
    triggerOrder?: TriggerOrder[] | null;
    totalDelay: number;
    route?: TripRouteSchema | null;
    events?: {
        id: number;
        eventType: `${EventTypeEnum}`;
        latitude: string;
        longitude: string;
        timestamp: string;
        value: string;
        metadata?:
            | {
                  zoneExternalId?: string;
                  duration?: number;
                  minSpeed?: number;
                  maxSpeed?: number;
                  info?: string;
              }
            | {
                  previousStatus?: TachographDriverStatus;
                  newStatus?: TachographDriverStatus;
                  startTime?: string;
                  endTime?: string;
                  cardId?: string;
              };
    }[];
    reportingType?: ReportingTypeEnum;
    tags?: string[] | ITagsSchema[];
    rateId?: string;
    services?: ITripServiceSchema[];
    errors?: ITripErrors[];
    createdAt?: string;
    updatedAt?: string;
    isExternal?: boolean;
    mobileAppEvents?: IEventSchema[];
    startNextTripOnPrevFinish?: boolean;
}
